import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import type { SegmentDetailOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { SecondsRange } from '@/components/common/SecondsRange';
import { orDash } from '@/components/common/show/shared';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { SEGMENT_SHOW_TABS } from '../constants';
import type { SegmentShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<SegmentShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface SegmentShowHeaderProps {
  record: SegmentDetailOut;
  activeTab: SegmentShowTab;
  onTabChange: (key: string) => void;
}

export const SegmentShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: SegmentShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long UUIDs wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            {record.segment_uuid}
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('progress.segments.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('progress.segments.fields.product_kind')}>
              <ProductFormatTag value={record.product_kind} labels="segments" />
            </StatCell>
            <StatCell label={t('progress.segments.fields.product_variant')}>
              <ProductVariantTag value={record.product_variant} />
            </StatCell>
            <StatCell label={t('progress.segments.sections.progression')}>
              {`${record.total_start_progression.toFixed(8)} → ${record.total_end_progression.toFixed(8)}`}
            </StatCell>
            <StatCell label={t('progress.segments.fields.fragment_range')}>
              {orDash(
                record.fragment_range && (
                  <SecondsRange
                    value={record.fragment_range}
                    hint={t('progress.segments.hints.fragment_range')}
                  />
                )
              )}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={SEGMENT_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
